'use client';

/**
 * A Reputation status emblem (lib/statusEmblem.ts draws it) and the chip that
 * shows it beside a name.
 *
 * The emblem is an <img> of a data: URI rather than inline SVG, so each one
 * keeps its own gradient ids however many are on a page. Mythic emblems turn
 * and glow slowly; with "reduce motion" they stand still.
 */

import { useEffect, useState } from 'react';
import { statusEmblemUri, statusName, bandIndex } from '@/lib/statusEmblem';
import type { ChipStatus } from '@/lib/status';

function useReducedMotion(): boolean {
  const [reduce, setReduce] = useState(false);
  useEffect(() => {
    const m = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!m) return;
    setReduce(m.matches);
    const on = () => setReduce(m.matches);
    m.addEventListener?.('change', on);
    return () => m.removeEventListener?.('change', on);
  }, []);
  return reduce;
}

export default function StatusEmblem({ n, size = 20, className = '', label }: { n: number; size?: number; className?: string; label?: string }) {
  const reduce = useReducedMotion();
  return (
    // eslint-disable-next-line @next/next/no-img-element -- a generated SVG, already sized
    <img src={statusEmblemUri(n, { size, animate: !reduce })} width={size} height={size} draggable={false}
         alt={label ?? statusName(n)} className={'shrink-0 select-none ' + className} style={{ width: size, height: size }} />
  );
}

/** The band's text colour, light and dark (Fallen … Mythic). */
const BAND_TEXT = [
  'text-rose-700 dark:text-rose-300',
  'text-slate-600 dark:text-slate-300',
  'text-amber-800 dark:text-amber-300',
  'text-stone-600 dark:text-stone-300',
  'text-orange-700 dark:text-orange-300',
  'text-slate-500 dark:text-slate-200',
  'text-yellow-700 dark:text-yellow-300',
  'text-sky-800 dark:text-sky-200',
  'text-cyan-700 dark:text-cyan-300',
  'text-violet-700 dark:text-violet-300',
];
export const bandTextClass = (n: number): string => BAND_TEXT[bandIndex(n)];

/** The status beside a name: its emblem and name, in the band's colour. */
export function StatusChip({ status, className = '' }: { status: ChipStatus; className?: string }) {
  return (
    <span title={`${status.name}: status ${status.n} of 50 (${status.band})`}
          className={'inline-flex shrink-0 items-center gap-0.5 text-[9.5px] font-bold uppercase leading-[15px] tracking-wide ' + bandTextClass(status.n) + ' ' + className}>
      <StatusEmblem n={status.n} size={18} label="" />
      {status.name}
    </span>
  );
}
